import React, { useState } from "react";
import { Award, Check, GraduationCap, Infinity as InfinityIcon, Languages, RefreshCw, Rocket, ShieldCheck, Sparkles, Blocks } from "lucide-react";
import { ProductCard } from "./ProductCard";
import { BundleCard } from "./GamesSection";
import { Reveal } from "./Reveal";

const OWNERSHIP_POINTS = [
  { icon: ShieldCheck, text: "Kupujesz raz — aplikacja jest Twoja na zawsze" },
  { icon: Award, text: "Pełne wsparcie techniczne zawsze w cenie" },
  { icon: RefreshCw, text: "Comiesięczne aktualizacje — darmowe na zawsze" },
  { icon: Rocket, text: "Każda aktualizacja rozwija możliwości Twojej aplikacji" },
];

const COMPARE_ROWS = [
  ["Płatność", "Co miesiąc, w nieskończoność", "Raz — i jest Twoja na zawsze"],
  ["Po rezygnacji", "Tracisz dostęp do wszystkiego", "Aplikacja zostaje Twoja na zawsze"],
  ["Koszt po 2 latach", "od ~700 zł i więcej", "od 39,99 zł — koniec wydatków"],
  ["Aktualizacje", "Kończą się wraz z płatnościami", "Co miesiąc, w cenie, na zawsze"],
  ["Wsparcie techniczne", "Zależne od planu", "Pełne, zawsze w cenie"],
];

const SUB_TABS = [
  { key: "all", label: "Wszystkie", icon: Sparkles },
  { key: "jezyki-obce", label: "Języki obce", icon: Languages },
  { key: "dla-dzieci", label: "Dla dzieci", icon: Blocks },
  { key: "ai-i-rozwoj", label: "AI i rozwój", icon: GraduationCap },
];

export function AppsSection({ apps }) {
  const [sub, setSub] = useState("all");
  const clean = apps.filter((a) => a.subcategory !== "gry-edukacyjne" && a.subcategory !== "pakiety"); // gry i pakiety mają osobne miejsca
  const bundleApps = apps.find((a) => a.id === "pakiet-aplikacje");
  const shown = sub === "all" ? clean : clean.filter((a) => a.subcategory === sub);
  if (!apps.length) return null;
  return (
    <section id="aplikacje" className="mx-auto max-w-7xl scroll-mt-24 px-4 py-16 sm:px-6" data-testid="apps-section">
      <Reveal>
        <div className="relative overflow-hidden rounded-3xl border border-fuchsia-500/20">
          <div className="relative h-64 sm:h-80">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/b348fc99-e0f8-4fe9-be9c-6f9ed6648170/images/e139c4e5398b9a750bc69384a4661118fbf4b447d07a1ec31ec0763266459c80.jpeg"
              alt="Interaktywne aplikacje edukacyjne"
              className="h-full w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-slate-950/95 via-slate-950/60 to-transparent" />
            <div className="absolute inset-0 flex flex-col justify-center px-6 sm:px-12">
              <span className="w-fit rounded-full bg-gradient-to-r from-fuchsia-400 to-cyan-400 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-950">
                Nowa kategoria
              </span>
              <h2 className="mt-4 font-display text-2xl font-extrabold text-white sm:text-4xl">
                Interaktywne <span className="gradient-text">aplikacje edukacyjne</span>
              </h2>
              <p className="mt-2 max-w-lg text-sm text-slate-300 sm:text-base">
                Nauka języków, edukacja dzieci i wirtualny korepetytor AI — osobny świat obok ebooków i audiobooków.
              </p>
              <div className="mt-5 flex flex-wrap gap-2">
                {[
                  { icon: Languages, label: "Nauka języków" },
                  { icon: Blocks, label: "Edukacja dzieci" },
                  { icon: GraduationCap, label: "Wirtualny korepetytor AI" },
                ].map((c) => {
                  const Icon = c.icon;
                  return (
                    <span key={c.label} className="flex items-center gap-1.5 rounded-full bg-slate-950/60 border border-cyan-400/30 px-3.5 py-1.5 text-[11px] font-semibold text-cyan-200 backdrop-blur">
                      <Icon className="h-3.5 w-3.5" /> {c.label}
                    </span>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      <Reveal delay={120}>
        <div className="glow-border mt-10 rounded-3xl p-[2px]" data-testid="ownership-ad">
          <div className="relative overflow-hidden rounded-3xl bg-slate-950/95 px-6 py-10 sm:px-12">
            <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-teal-500/15 blur-3xl" />
            <div className="relative grid items-center gap-10 lg:grid-cols-2">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full border border-teal-500/40 bg-teal-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-teal-300">
                  <InfinityIcon className="h-3.5 w-3.5" /> Na zawsze, z darmowymi aktualizacjami
                </span>
                <h3 className="mt-5 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">
                  Kupujesz raz. <span className="gradient-text">Masz na zawsze.</span>
                </h3>
                <div className="mt-6 space-y-3">
                  {OWNERSHIP_POINTS.map((p, i) => {
                    const Icon = p.icon;
                    return (
                      <div key={i} className="flex items-start gap-3">
                        <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-teal-400 to-cyan-500">
                          <Icon className="h-3.5 w-3.5 text-slate-950" />
                        </span>
                        <p className="text-sm leading-relaxed text-slate-300">{p.text}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="overflow-hidden rounded-2xl border border-cyan-500/15">
                <table className="w-full text-left text-xs sm:text-sm" data-testid="ownership-compare-table">
                  <thead>
                    <tr className="bg-slate-900/60 text-slate-400">
                      <th className="px-4 py-3 font-semibold"></th>
                      <th className="px-4 py-3 font-semibold">U innych — opłaty co miesiąc</th>
                      <th className="px-4 py-3 font-bold text-cyan-300">Aplikacje DigiLib</th>
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARE_ROWS.map((r, i) => (
                      <tr key={i} className="border-t border-slate-800/60">
                        <td className="px-4 py-3 font-semibold text-slate-300">{r[0]}</td>
                        <td className="px-4 py-3 text-slate-500">{r[1]}</td>
                        <td className="px-4 py-3">
                          <span className="flex items-start gap-1.5 font-medium text-teal-300">
                            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {r[2]}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </Reveal>

      <div className="mt-10 flex flex-wrap gap-2.5" data-testid="apps-subcategory-tabs">
        {SUB_TABS.map((t) => {
          const Icon = t.icon;
          const isActive = sub === t.key;
          const count = t.key === "all" ? clean.length : clean.filter((a) => a.subcategory === t.key).length;
          return (
            <button
              key={t.key}
              data-testid={`apps-sub-${t.key}`}
              onClick={() => setSub(t.key)}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-[background-color,color,transform,border-color] duration-200 hover:-translate-y-0.5 ${isActive ? "text-slate-950 shadow-lg" : "glass-panel text-slate-300 hover:border-fuchsia-400/40"}`}
              style={isActive ? { background: "linear-gradient(90deg, #e879f9, #22d3ee)" } : {}}
            >
              <Icon className="h-4 w-4" /> {t.label}
              <span className={`rounded-full px-1.5 py-0.5 text-[10px] ${isActive ? "bg-slate-950/20" : "bg-slate-800"}`}>{count}</span>
            </button>
          );
        })}
      </div>

      <div key={sub} className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6 lg:grid-cols-5" data-testid="apps-grid">
        {shown.map((p, i) => (
          <Reveal key={p.id} delay={Math.min(i, 8) * 70}>
            <ProductCard product={p} />
          </Reveal>
        ))}
      </div>
      <Reveal delay={200}>
        <p className="mt-8 flex items-center justify-center gap-2 text-center text-sm text-slate-500">
          <Sparkles className="h-4 w-4 text-fuchsia-400" />
          Kolejne języki i przedmioty już w produkcji — aplikacje, które kupisz dziś, dostaną je w aktualizacjach.
        </p>
      </Reveal>

      {/* PAKIET APLIKACJI — pod produktami kategorii */}
      {bundleApps && (
        <div className="mt-10">
          <BundleCard
            testid="bundle-apps-card"
            img="/img/tiles/aplikacje.webp"
            badge={<><Rocket className="h-3.5 w-3.5" /> Pakiet wszystkich aplikacji</>}
            title="Wszystkie 9 aplikacji edukacyjnych w jednym pakiecie"
            desc="Pięć kursów DigiLingua, dwie aplikacje DigiKids, DigiTutor AI i DigiMind — każda kupiona raz jest Twoja na zawsze, z comiesięcznymi aktualizacjami."
            items={["DigiLingua ×5", "DigiKids ×2", "DigiTutor AI", "DigiMind"]}
            price="229,99"
            oldPrice="464,91 zł"
            note="Oszczędzasz ponad 230 zł względem zakupu osobno"
            soon
          />
        </div>
      )}
    </section>
  );
}
